import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkActionToolbar = ({ selectedAuctions, onBulkAction, onClearSelection }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);

  const bulkActions = [
    { value: '', label: 'Select action...' },
    { value: 'pause', label: 'Pause Selected', icon: 'Pause', variant: 'warning' },
    { value: 'extend', label: 'Extend Time (+2min)', icon: 'Clock', variant: 'outline' },
    { value: 'force_close', label: 'Force Close', icon: 'Square', variant: 'destructive' },
    { value: 'manual_route', label: 'Manual Route', icon: 'Route', variant: 'secondary' },
    { value: 'export', label: 'Export Data', icon: 'Download', variant: 'outline' },
    { value: 'archive', label: 'Archive', icon: 'Archive', variant: 'secondary' }
  ];

  const handleActionSelect = (value) => {
    setSelectedAction(value);
    if (value && ['pause', 'force_close', 'manual_route']?.includes(value)) {
      setShowConfirmation(true);
    } else if (value) {
      executeAction(value);
    }
  };

  const executeAction = async (action) => {
    setActionInProgress(true);
    try {
      await onBulkAction(action, selectedAuctions);
      setSelectedAction('');
      setShowConfirmation(false);
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setActionInProgress(false);
    }
  };

  const getActionDetails = (action) => {
    const actionConfig = bulkActions?.find(a => a?.value === action);
    return actionConfig || {};
  };

  const getImpactMessage = (action) => {
    switch (action) {
      case 'pause':
        return `This will pause ${selectedAuctions?.length} active auctions. Buyers will be notified and bidding will stop.`;
      case 'force_close':
        return `This will immediately close ${selectedAuctions?.length} auctions and route calls to current highest bidders.`;
      case 'manual_route':
        return `This will override automatic routing for ${selectedAuctions?.length} auctions and require manual buyer selection.`;
      default:
        return `This action will be applied to ${selectedAuctions?.length} selected auctions.`;
    }
  };

  if (selectedAuctions?.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="CheckSquare" size={16} className="text-accent" />
              <span className="text-sm font-medium text-foreground">
                {selectedAuctions?.length} auction{selectedAuctions?.length !== 1 ? 's' : ''} selected
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <Select
                options={bulkActions}
                value={selectedAction}
                onChange={handleActionSelect}
                placeholder="Choose action..."
                disabled={actionInProgress}
                className="w-48"
              />
              
              {selectedAction && !showConfirmation && (
                <Button
                  variant={getActionDetails(selectedAction)?.variant || 'default'}
                  size="sm"
                  iconName={getActionDetails(selectedAction)?.icon}
                  onClick={() => executeAction(selectedAction)}
                  loading={actionInProgress}
                >
                  Execute
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              disabled={actionInProgress}
            >
              Clear Selection
            </Button>
            
            <div className="text-xs text-muted-foreground">
              Keyboard: Ctrl+A (select all), Esc (clear)
            </div>
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-accent/20">
          <span className="text-xs text-muted-foreground mr-2">Quick actions:</span>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Pause"
            onClick={() => handleActionSelect('pause')}
            disabled={actionInProgress}
          >
            Pause All
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Clock"
            onClick={() => executeAction('extend')}
            disabled={actionInProgress}
          >
            +2 Min
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={() => executeAction('export')}
            disabled={actionInProgress}
          >
            Export
          </Button>
        </div>
      </div>
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-1100 flex items-center justify-center">
          <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-2 rounded-lg ${
                  selectedAction === 'force_close' ? 'bg-error/10' : 'bg-warning/10'
                }`}>
                  <Icon 
                    name={getActionDetails(selectedAction)?.icon} 
                    size={20} 
                    className={selectedAction === 'force_close' ? 'text-error' : 'text-warning'} 
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Confirm Bulk Action
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {getActionDetails(selectedAction)?.label}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-foreground mb-3">
                  {getImpactMessage(selectedAction)}
                </p>
                
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-xs text-muted-foreground mb-2">Selected auctions:</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedAuctions?.slice(0, 5)?.map((auctionId) => (
                      <div key={auctionId} className="text-xs font-mono text-foreground">
                        {auctionId}
                      </div>
                    ))}
                    {selectedAuctions?.length > 5 && (
                      <div className="text-xs text-muted-foreground">
                        ... and {selectedAuctions?.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowConfirmation(false);
                    setSelectedAction('');
                  }}
                  disabled={actionInProgress}
                >
                  Cancel
                </Button>
                <Button
                  variant={getActionDetails(selectedAction)?.variant || 'default'}
                  onClick={() => executeAction(selectedAction)}
                  loading={actionInProgress}
                  iconName={getActionDetails(selectedAction)?.icon}
                >
                  Confirm {getActionDetails(selectedAction)?.label}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkActionToolbar;